import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  CheckSquare,
  Search,
  MoreHorizontal,
  Eye,
  User,
  Clock,
  Loader2,
  Play,
  CheckCircle,
  List,
  LayoutGrid,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

const statusConfig = {
  open: { label: 'Open', class: 'bg-blue-100 text-blue-700', column: 0 },
  assigned: { label: 'Assigned', class: 'bg-purple-100 text-purple-700', column: 1 },
  in_progress: { label: 'In Progress', class: 'bg-cyan-100 text-cyan-700', column: 2 },
  waiting_external: { label: 'Waiting', class: 'bg-amber-100 text-amber-700', column: 2 },
  completed: { label: 'Completed', class: 'bg-green-100 text-green-700', column: 3 },
  closed: { label: 'Closed', class: 'bg-slate-100 text-slate-700', column: 3 },
  cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-700', column: 3 },
};

const taskTypeConfig = {
  review: { label: 'Review', icon: Eye },
  acknowledgment: { label: 'Acknowledgment', icon: CheckCircle },
  followup: { label: 'Follow-up', icon: Clock },
  manual_correction: { label: 'Manual Correction', icon: AlertTriangle },
};

export const TaskBoardPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('kanban');
  const [filters, setFilters] = useState({
    status: '',
    task_type: '',
    assigned_user_id: '',
  });

  useEffect(() => {
    fetchTasks();
  }, [filters]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', 100);
      if (filters.status) params.append('status', filters.status);
      if (filters.task_type) params.append('task_type', filters.task_type);

      const response = await api.get(`/tasks?${params.toString()}`);
      setTasks(response.data.tasks || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await api.patch(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Task updated');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getTasksByColumn = (columnIndex) => {
    return tasks.filter((task) => {
      const config = statusConfig[task.status];
      return config?.column === columnIndex;
    });
  };

  const columns = [
    { title: 'Open', statuses: ['open'], color: 'border-blue-400' },
    { title: 'Assigned', statuses: ['assigned'], color: 'border-purple-400' },
    { title: 'In Progress', statuses: ['in_progress', 'waiting_external'], color: 'border-cyan-400' },
    { title: 'Done', statuses: ['completed', 'closed', 'cancelled'], color: 'border-green-400' },
  ];

  const TaskCard = ({ task }) => {
    const TypeIcon = taskTypeConfig[task.task_type]?.icon || CheckSquare;
    const isOverdue = task.due_at && new Date(task.due_at) < new Date();

    return (
      <Card 
        className="mb-3 card-interactive"
        data-testid={`task-card-${task.task_id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-2">
            <Badge className={statusConfig[task.status]?.class}>
              {statusConfig[task.status]?.label}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {task.report_id && (
                  <DropdownMenuItem asChild>
                    <Link to={`/reports/${task.report_id}`}>
                      <Eye className="w-4 h-4 mr-2" />
                      View Report
                    </Link>
                  </DropdownMenuItem>
                )}
                {task.status === 'open' && (
                  <DropdownMenuItem onClick={() => handleUpdateStatus(task.task_id, 'assigned')}>
                    <User className="w-4 h-4 mr-2" />
                    Assign to Me
                  </DropdownMenuItem>
                )}
                {task.status === 'assigned' && (
                  <DropdownMenuItem onClick={() => handleUpdateStatus(task.task_id, 'in_progress')}>
                    <Play className="w-4 h-4 mr-2" />
                    Start
                  </DropdownMenuItem>
                )}
                {task.status === 'in_progress' && (
                  <DropdownMenuItem onClick={() => handleUpdateStatus(task.task_id, 'completed')}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <TypeIcon className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{taskTypeConfig[task.task_type]?.label}</span>
          </div>

          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {task.description}
          </p>

          {task.patient_name && (
            <p className="text-xs text-muted-foreground mb-2">
              Patient: {task.patient_name}
            </p>
          )}

          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1">
              {isOverdue && task.status !== 'completed' && task.status !== 'closed' ? (
                <span className="text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Overdue
                </span>
              ) : task.due_at ? (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Due {formatDistanceToNow(new Date(task.due_at), { addSuffix: true })}
                </span>
              ) : null}
            </div>
            {task.escalation_level > 0 && (
              <Badge variant="outline" className="bg-red-50 text-red-700 text-xs">
                ESC {task.escalation_level}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="task-board-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Task Board</h1>
          <p className="text-muted-foreground mt-1">{total} tasks total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'kanban' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('kanban')}
            data-testid="kanban-view-btn"
          >
            <LayoutGrid className="w-4 h-4 mr-1" />
            Kanban
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            data-testid="list-view-btn"
          >
            <List className="w-4 h-4 mr-1" />
            List
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search tasks..." className="pl-10" data-testid="search-input" />
            </div>
            <Select
              value={filters.task_type || "all"}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, task_type: value === "all" ? "" : value }))}
            >
              <SelectTrigger className="w-[160px]" data-testid="type-filter">
                <SelectValue placeholder="Task Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="review">Review</SelectItem>
                <SelectItem value="acknowledgment">Acknowledgment</SelectItem>
                <SelectItem value="followup">Follow-up</SelectItem>
                <SelectItem value="manual_correction">Manual Correction</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Task Board */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : viewMode === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {columns.map((column, index) => (
            <div key={column.title} className="flex flex-col">
              <div className={`border-t-4 ${column.color} rounded-t-lg bg-slate-50 p-3 mb-2`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">{column.title}</h3>
                  <Badge variant="outline" className="text-xs">
                    {getTasksByColumn(index).length}
                  </Badge>
                </div>
              </div>
              <ScrollArea className="flex-1 min-h-[400px] max-h-[600px]">
                <div className="space-y-3 pr-2">
                  {getTasksByColumn(index).map((task) => (
                    <TaskCard key={task.task_id} task={task} />
                  ))}
                  {getTasksByColumn(index).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No tasks
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Patient</th>
                    <th>Status</th>
                    <th>Due</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => {
                    const TypeIcon = taskTypeConfig[task.task_type]?.icon || CheckSquare;
                    return (
                      <tr key={task.task_id}>
                        <td>
                          <div className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-muted-foreground" />
                            <span>{taskTypeConfig[task.task_type]?.label}</span>
                          </div>
                        </td>
                        <td className="max-w-xs truncate">{task.description}</td>
                        <td>{task.patient_name || '-'}</td>
                        <td>
                          <Badge className={statusConfig[task.status]?.class}>
                            {statusConfig[task.status]?.label}
                          </Badge>
                        </td>
                        <td className="text-sm text-muted-foreground">
                          {task.due_at ? formatDistanceToNow(new Date(task.due_at), { addSuffix: true }) : '-'}
                        </td>
                        <td>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/reports/${task.report_id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
